/* ============================================================================
   IPERTECH 2026 — JAVASCRIPT
   ----------------------------------------------------------------------------
   Logica frontend per i moduli premium del tema "Ipertech Ufficiale".
   Async, defer-loaded, vanilla JS (zero dipendenze).
   - Sticky ATC bar visibility logic + AJAX add-to-cart + toast feedback
   - Social proof viewers/carted live
   - Smart bundle calc, sconto auto, AJAX add multi-item
   - Cart icon bump + fly animation
   - Toast notifications
   - Skeleton loader auto-replace
   ============================================================================ */
(function () {
  'use strict';

  // ============================================================================
  // 1. UTILITIES
  // ============================================================================
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const formatMoney = (cents) => {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    const symbol = window.IpertechSettings?.currencySymbol || '€';
    const value = (cents / 100).toFixed(2).replace('.', ',');
    return `${symbol}${value}`;
  };

  const debounce = (fn, wait = 200) => {
    let t;
    return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); };
  };

  const reducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ============================================================================
  // 2. TOAST NOTIFICATIONS
  // ============================================================================
  let toastEl = null;
  let toastTimer = null;

  const showToast = (message, variant = 'success') => {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ipertech-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }

    const icon =
      variant === 'success'
        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>'
        : variant === 'error'
        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>'
        : '';

    toastEl.className = `ipertech-toast ipertech-toast--${variant}`;
    toastEl.innerHTML = `<span class="ipertech-toast__icon" aria-hidden="true">${icon}</span><span>${message}</span>`;

    requestAnimationFrame(() => {
      toastEl.setAttribute('data-visible', 'true');
    });

    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toastEl.removeAttribute('data-visible');
    }, 3200);
  };

  // ============================================================================
  // 3. CART ICON ANIMATION
  // ============================================================================
  const findCartIcon = () => {
    return (
      $('[data-ipertech-cart-icon]') ||
      $('a[href$="/cart"] svg') ||
      $('header-drawer ~ * a[href$="/cart"]') ||
      $('.header__icon--cart svg') ||
      $('a.header__icon--cart') ||
      $('cart-icon-bubble')
    );
  };

  const bumpCartIcon = () => {
    const icon = findCartIcon();
    if (!icon) return;
    icon.classList.remove('ipertech-cart-bump');
    void icon.offsetWidth;
    icon.classList.add('ipertech-cart-bump');
    setTimeout(() => icon.classList.remove('ipertech-cart-bump'), 700);
  };

  const flyToCart = (originEl) => {
    if (reducedMotion() || !originEl) return;
    const cartIcon = findCartIcon();
    if (!cartIcon) return;
    const startRect = originEl.getBoundingClientRect();
    const endRect = cartIcon.getBoundingClientRect();
    const fly = document.createElement('div');
    fly.className = 'ipertech-cart-fly';
    fly.setAttribute('aria-hidden', 'true');
    fly.innerHTML = '+';
    fly.style.left = startRect.left + startRect.width / 2 - 25 + 'px';
    fly.style.top = startRect.top + startRect.height / 2 - 25 + 'px';
    document.body.appendChild(fly);

    requestAnimationFrame(() => {
      const dx = endRect.left + endRect.width / 2 - (startRect.left + startRect.width / 2);
      const dy = endRect.top + endRect.height / 2 - (startRect.top + startRect.height / 2);
      fly.style.transform = `translate(${dx}px, ${dy}px) scale(0.2)`;
      fly.style.opacity = '0';
    });
    setTimeout(() => fly.remove(), 750);
  };

  // ============================================================================
  // 4. CART API (Shopify AJAX)
  // ============================================================================
  const cartApi = {
    add: async (items) => {
      const arr = Array.isArray(items) ? items : [items];
      const res = await fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
        },
        body: JSON.stringify({ items: arr }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Errore aggiunta carrello');
      }
      return res.json();
    },
    get: async () => {
      const res = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });
      return res.json();
    },
  };

  const refreshCartUI = async () => {
    try {
      const cart = await cartApi.get();
      const counters = $$('[data-cart-count], cart-icon-bubble [data-count], .cart-count-bubble span[aria-hidden="true"]');
      counters.forEach((c) => {
        c.textContent = cart.item_count;
        if (cart.item_count > 0) c.removeAttribute('hidden');
      });
      // Notify Dawn cart drawer
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
      // PUB events for any listener
      window.dispatchEvent(new CustomEvent('ipertech:cart:updated', { detail: cart }));
    } catch (e) {
      // Silenzioso
    }
  };

  // ============================================================================
  // 5. STICKY ATC BAR
  // ============================================================================
  const initStickyAtc = () => {
    const bar = $('[data-ipertech-sticky]');
    if (!bar) return;

    bar.removeAttribute('hidden');
    const showAfter = parseInt(bar.dataset.showAfter, 10) || 600;

    // Trova il bottone ATC principale del prodotto per nasconderci sopra
    const mainAtc =
      $('product-form button[type="submit"][name="add"]') ||
      $('.product-form__buttons button[name="add"]') ||
      $('button[name="add"]:not(.ipertech-sticky-atc__btn):not(.ipertech-bundle__cta)');

    let mainAtcVisible = true;
    if (mainAtc && 'IntersectionObserver' in window) {
      const obs = new IntersectionObserver(
        (entries) => {
          mainAtcVisible = entries[0].isIntersecting;
          updateVisibility();
        },
        { threshold: 0.15 }
      );
      obs.observe(mainAtc);
    } else {
      mainAtcVisible = false;
    }

    const updateVisibility = () => {
      const scrolled = window.scrollY > showAfter;
      const shouldShow = scrolled && !mainAtcVisible;
      bar.setAttribute('data-visible', shouldShow ? 'true' : 'false');
    };

    window.addEventListener('scroll', debounce(updateVisibility, 60), { passive: true });
    window.addEventListener('resize', debounce(updateVisibility, 200));
    updateVisibility();

    // Variant select sync
    const select = $('[data-ipertech-sticky-variant-select]', bar);
    const hidden = $('[data-ipertech-sticky-variant-input]', bar);
    const priceWrap = $('[data-ipertech-sticky-price]', bar);
    const cta = $('[data-ipertech-sticky-cta]', bar);

    if (select && hidden) {
      select.addEventListener('change', (e) => {
        const opt = e.target.options[e.target.selectedIndex];
        hidden.value = opt.value;
        if (priceWrap) {
          const price = parseInt(opt.dataset.price, 10);
          const compareAt = parseInt(opt.dataset.compareAt, 10);
          priceWrap.innerHTML = '';
          if (compareAt > price) {
            const oldEl = document.createElement('span');
            oldEl.className = 'ipertech-sticky-atc__price-old';
            oldEl.textContent = formatMoney(compareAt);
            priceWrap.appendChild(oldEl);
          }
          const nowEl = document.createElement('span');
          nowEl.className = 'ipertech-sticky-atc__price-now';
          nowEl.textContent = formatMoney(price);
          priceWrap.appendChild(nowEl);
        }
        if (cta) {
          const available = opt.dataset.available === 'true';
          cta.disabled = !available;
          const lbl = $('.ipertech-sticky-atc__btn-label', cta);
          if (lbl) {
            lbl.textContent = available
              ? bar.dataset.labelAdd || 'Aggiungi al carrello'
              : bar.dataset.labelOos || 'Esaurito';
          }
        }
      });
    }

    // Form submission con AJAX
    const form = $('#IpertechStickyForm');
    if (form && cta) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (cta.disabled) return;
        const variantId = hidden ? hidden.value : null;
        if (!variantId) return;

        cta.dataset.loading = 'true';
        const liveRegion = $('[data-ipertech-sticky-live]', bar);

        try {
          await cartApi.add({ id: parseInt(variantId, 10), quantity: 1 });
          flyToCart(cta);
          bumpCartIcon();
          showToast('Aggiunto al carrello!', 'success');
          if (liveRegion) liveRegion.textContent = 'Prodotto aggiunto al carrello';
          await refreshCartUI();
        } catch (err) {
          showToast(err.message || 'Errore. Riprova.', 'error');
          if (liveRegion) liveRegion.textContent = 'Errore aggiunta al carrello';
        } finally {
          cta.dataset.loading = 'false';
        }
      });
    }
  };

  // ============================================================================
  // 6. SOCIAL PROOF
  // ============================================================================
  const initSocialProof = () => {
    const container = $('[data-ipertech-socialproof]');
    if (!container) return;

    const productId = container.dataset.productId;
    const viewersBase = parseInt(container.dataset.viewersBase, 10) || 0;
    const storageKey = `ipertech_sp_${productId}`;

    // Viewers live (combinazione base + jitter realistico per sessione)
    const viewersRow = $('[data-ipertech-sp-viewers]', container);
    const viewersCountEl = $('[data-ipertech-sp-viewers-count]', container);

    if (viewersRow && viewersCountEl) {
      let stored;
      try { stored = JSON.parse(sessionStorage.getItem(storageKey) || 'null'); } catch (e) {}
      const seed = stored?.seed || (Date.now() % 13 + 7);
      const generateViewers = () => {
        if (viewersBase > 0) {
          const variation = Math.floor(Math.random() * 7) - 3;
          return Math.max(2, viewersBase + variation);
        }
        return Math.floor(seed + Math.random() * 8) + 4;
      };

      try {
        sessionStorage.setItem(storageKey, JSON.stringify({ seed, ts: Date.now() }));
      } catch (e) {}

      const updateViewers = () => {
        viewersCountEl.textContent = generateViewers();
        viewersRow.removeAttribute('hidden');
      };

      setTimeout(updateViewers, 800);
      const cycle = () => {
        setTimeout(() => {
          updateViewers();
          cycle();
        }, 15000 + Math.random() * 10000);
      };
      cycle();
    }

    const cartedRow = $('[data-ipertech-sp-carted]', container);
    const cartedCountEl = $('[data-ipertech-sp-carted-count]', container);

    if (cartedRow && cartedCountEl) {
      const cartedStorageKey = `ipertech_carted_${productId}`;
      const today = new Date().toDateString();

      let cartedData;
      try {
        cartedData = JSON.parse(localStorage.getItem(cartedStorageKey) || 'null');
        if (!cartedData || cartedData.day !== today) {
          cartedData = { day: today, count: 3 + Math.floor(Math.random() * 14) };
          localStorage.setItem(cartedStorageKey, JSON.stringify(cartedData));
        }
      } catch (e) {
        cartedData = { count: 5 };
      }

      cartedCountEl.textContent = cartedData.count;
      cartedRow.removeAttribute('hidden');

      window.addEventListener('ipertech:cart:updated', () => {
        try {
          cartedData.count = (cartedData.count || 0) + 1;
          localStorage.setItem(cartedStorageKey, JSON.stringify(cartedData));
          cartedCountEl.textContent = cartedData.count;
        } catch (e) {}
      });
    }
  };

  // ============================================================================
  // 7. SMART BUNDLE
  // ============================================================================
  const initBundle = () => {
    const bundle = $('[data-ipertech-bundle]');
    if (!bundle) return;

    const items = $$('[data-ipertech-bundle-item]', bundle);
    const subtotalEl = $('[data-ipertech-bundle-subtotal]', bundle);
    const totalEl = $('[data-ipertech-bundle-total]', bundle);
    const discountRowEl = $('[data-ipertech-bundle-discount-row]', bundle);
    const discountAmountEl = $('[data-ipertech-bundle-discount-amount]', bundle);
    const saveEl = $('[data-ipertech-bundle-save]', bundle);
    const saveAmountEl = $('[data-ipertech-bundle-save-amount]', bundle);
    const cta = $('[data-ipertech-bundle-cta]', bundle);
    const liveRegion = $('[data-ipertech-bundle-live]', bundle);
    const discountPct = parseFloat(bundle.dataset.discountPct) || 0;

    const recalc = () => {
      let subtotal = 0;
      let count = 0;
      items.forEach((item) => {
        const checked = item.dataset.checked === 'true';
        if (checked) {
          subtotal += parseInt(item.dataset.price, 10) || 0;
          count++;
        }
      });

      const allSelected = count === items.length;
      const discountActive = allSelected && discountPct > 0 && count >= 2;
      const discountAmount = discountActive ? Math.round(subtotal * (discountPct / 100)) : 0;
      const total = subtotal - discountAmount;

      if (subtotalEl) subtotalEl.textContent = formatMoney(subtotal);
      if (totalEl) totalEl.textContent = formatMoney(total);

      if (discountRowEl) {
        if (discountActive) {
          discountRowEl.removeAttribute('hidden');
          if (discountAmountEl) discountAmountEl.textContent = '−' + formatMoney(discountAmount);
        } else {
          discountRowEl.setAttribute('hidden', '');
        }
      }

      if (saveEl) {
        if (discountActive) {
          saveEl.removeAttribute('hidden');
          if (saveAmountEl) saveAmountEl.textContent = formatMoney(discountAmount);
        } else {
          saveEl.setAttribute('hidden', '');
        }
      }

      if (cta) {
        cta.disabled = count === 0;
        if (liveRegion) {
          liveRegion.textContent = `Bundle aggiornato: ${count} articoli, totale ${formatMoney(total)}`;
        }
      }
    };

    items.forEach((item) => {
      const cb = $('[data-ipertech-bundle-check]', item);
      if (!cb) return;
      cb.addEventListener('change', () => {
        if (item.dataset.locked === 'true') return;
        item.dataset.checked = cb.checked ? 'true' : 'false';
        recalc();
      });
    });

    recalc();

    if (cta) {
      cta.addEventListener('click', async () => {
        if (cta.disabled) return;
        const selectedItems = items.filter((i) => i.dataset.checked === 'true');
        if (selectedItems.length === 0) return;

        const cartItems = selectedItems.map((i) => ({
          id: parseInt(i.dataset.variantId, 10),
          quantity: 1,
        }));

        cta.dataset.loading = 'true';

        try {
          await cartApi.add(cartItems);
          flyToCart(cta);
          bumpCartIcon();
          showToast(`${cartItems.length} articoli aggiunti al carrello!`, 'success');
          if (liveRegion) liveRegion.textContent = 'Bundle aggiunto al carrello';
          await refreshCartUI();
        } catch (err) {
          showToast(err.message || 'Errore. Riprova.', 'error');
          if (liveRegion) liveRegion.textContent = 'Errore aggiunta bundle';
        } finally {
          cta.dataset.loading = 'false';
        }
      });
    }
  };

  // ============================================================================
  // 8. CART CLICK INTERCEPT
  // ============================================================================
  const initCartIntercept = () => {
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('[data-ipertech-bump-on-submit]')) return;
      setTimeout(() => bumpCartIcon(), 400);
    });

    document.addEventListener('cart:added', () => {
      bumpCartIcon();
    });
  };

  // ============================================================================
  // 9. SKELETON AUTO-REPLACE
  // ============================================================================
  const initSkeletonObserver = () => {
    if (!('MutationObserver' in window)) return;
    const obs = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          const skeleton = node.parentElement?.querySelector('[data-ipertech-skeleton]');
          if (skeleton && node !== skeleton && node.matches?.('[data-ipertech-replace-skeleton]')) {
            skeleton.style.transition = 'opacity 0.2s';
            skeleton.style.opacity = '0';
            setTimeout(() => skeleton.remove(), 220);
          }
        });
      });
    });
    obs.observe(document.body, { childList: true, subtree: true });
  };

  // ============================================================================
  // 10. INIT ALL
  // ============================================================================
  const init = () => {
    try { initStickyAtc(); } catch (e) { console.warn('[Ipertech] sticky atc:', e); }
    try { initSocialProof(); } catch (e) { console.warn('[Ipertech] social proof:', e); }
    try { initBundle(); } catch (e) { console.warn('[Ipertech] bundle:', e); }
    try { initCartIntercept(); } catch (e) { console.warn('[Ipertech] cart intercept:', e); }
    try { initSkeletonObserver(); } catch (e) { console.warn('[Ipertech] skeleton:', e); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.Ipertech = window.Ipertech || {};
  window.Ipertech.toast = showToast;
  window.Ipertech.bumpCart = bumpCartIcon;
  window.Ipertech.flyToCart = flyToCart;
  window.Ipertech.cartApi = cartApi;
  window.Ipertech.refreshCart = refreshCartUI;
})();
